import React, { useState, useEffect } from 'react';
import { useUpdateOrganizationSettings } from '@/hooks/useUpdateOrganizationSettings';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { formatPhoneNumber } from '@/lib/format';
import { timezones } from '@/utils/timezones';
import { useOrganizationCoreSettings } from '@/hooks/useOrganizationCoreSettings';

interface StudioInfoFormProps {
  organizationId: string | undefined;
  studioSettings: {
    studio_name: string;
    contact_email: string;
    contact_phone: string;
    timezone: string;
  };
  setStudioSettings: React.Dispatch<React.SetStateAction<{
    studio_name: string;
    contact_email: string;
    contact_phone: string;
    timezone: string;
  }>>;
}

const StudioInfoForm: React.FC<StudioInfoFormProps> = ({
  organizationId,
  studioSettings,
  setStudioSettings
}) => {
  console.log('🏢 StudioInfoForm received organizationId:', organizationId);
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formattedPhone, setFormattedPhone] = useState(studioSettings.contact_phone || '');
  const { organization, setOrganization, loading, error } = useOrganizationCoreSettings(organizationId);
  
  console.log('🏢 StudioInfoForm hook states:', {
    organizationId,
    organizationLoaded: !!organization,
    organizationName: organization?.name,
    loading,
    error: error?.message
  });
  
  const updateOrganizationSettings = useUpdateOrganizationSettings(organization, setOrganization, organizationId);
  
  // Set formatted phone when studio settings change
  useEffect(() => {
    if (studioSettings.contact_phone) {
      setFormattedPhone(formatPhoneNumber(studioSettings.contact_phone));
    } else {
      setFormattedPhone('');
    }
  }, [studioSettings.contact_phone]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setStudioSettings(prev => ({ ...prev, [name]: value }));
  };
  
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const formatted = formatPhoneNumber(value);
    setFormattedPhone(formatted);
    setStudioSettings(prev => ({ ...prev, contact_phone: formatted }));
  };

  const handleTimezoneChange = (value: string) => {
    setStudioSettings(prev => ({ ...prev, timezone: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    console.log('🏢 StudioInfoForm: Attempting to save with organizationId:', organizationId);
    
    if (!organizationId) {
      console.error('❌ StudioInfoForm: No organization ID found!');
      toast.error('No organization ID found');
      return;
    }
    
    if (!organization) {
      console.error('❌ StudioInfoForm: Organization data not loaded yet!');
      toast.error('Organization data not loaded. Please wait and try again.');
      return;
    }
    
    if (loading) {
      console.warn('⏳ StudioInfoForm: Still loading organization data');
      toast.error('Still loading organization data. Please wait.');
      return;
    }
    
    try {
      setIsSubmitting(true);
      
      await updateOrganizationSettings({
        name: studioSettings.studio_name,
        contact_email: studioSettings.contact_email,
        contact_phone: studioSettings.contact_phone,
        timezone: studioSettings.timezone,
      });

      toast.success('Studio information updated successfully');
    } catch (error) {
      console.error('Error updating studio information:', error);
      toast.error('Failed to update studio information');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Hive Information</CardTitle>
        <CardDescription>
          Update your organization's contact information
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="studio_name">Studio Name</Label>
            <Input
              id="studio_name"
              name="studio_name"
              value={studioSettings.studio_name}
              onChange={handleChange}
              placeholder="Dance Studio Name"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="contact_email">Contact Email</Label>
            <Input
              id="contact_email"
              name="contact_email"
              type="email"
              value={studioSettings.contact_email}
              onChange={handleChange}
              placeholder="studio@example.com"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="contact_phone">Contact Phone</Label>
            <Input
              id="contact_phone"
              name="contact_phone"
              type="tel"
              value={formattedPhone}
              onChange={handlePhoneChange}
              placeholder="(555) 123-4567"
              maxLength={14}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="timezone">Timezone</Label>
            <Select
              value={studioSettings.timezone}
              onValueChange={handleTimezoneChange}
            >
              <SelectTrigger id="timezone">
                <SelectValue placeholder="Select Timezone" />
              </SelectTrigger>
              <SelectContent>
                {timezones.map((tz) => (
                  <SelectItem key={tz.value} value={tz.value}>
                    {tz.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex justify-end">
            <Button type="submit" disabled={isSubmitting || loading || !organization}>
              {loading ? 'Loading...' : isSubmitting ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default StudioInfoForm;
